import React, { useCallback, useRef, useEffect, useState } from 'react';
import { Music, Zap, Volume2, VolumeX, Star, X, ExternalLink } from 'lucide-react';
import { useLiveFeed } from '../hooks/useLiveFeed';
import { playRoomActionSound, unlockAudio } from '../utils/roomActionSounds';

const API = process.env.REACT_APP_BACKEND_URL;

const ACTION_COLORS = {
  pop_your_up: '#EF4444',
  gu_them: '#F59E0B',
  one_mic_individual: '#3B82F6',
  one_mic_group: '#8B5CF6',
  come_back_goo: '#10B981',
  come_back_popped: '#EC4899',
  poll_leader: '#F59E0B',
};

const TickerRow = ({ items, icon: Icon, color, bgColor, borderColor, label, emptyText, renderItem, testId, flashColor, labelExtra }) => {
  const doubled = items.length > 0 ? [...items, ...items] : [];
  const duration = Math.max(items.length * 5, 20);

  return (
    <div
      className={`border-t ${borderColor} transition-colors duration-100`}
      data-testid={testId}
      style={flashColor ? {
        animation: 'ticker-flash 0.8s ease-out',
        '--flash-color': flashColor,
      } : undefined}
    >
      <div className="flex items-center">
        {/* Label */}
        <div className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 ${bgColor} border-r ${borderColor}`}>
          <Icon size={10} className={color} />
          <span className={`${color} text-[9px] font-bold tracking-wider uppercase whitespace-nowrap`}>{label}</span>
          {labelExtra}
        </div>
        {/* Marquee */}
        <div className="flex-1 overflow-hidden relative">
          {items.length === 0 ? (
            <div className="px-4 py-1.5">
              <span className="text-white/15 text-[10px]">{emptyText}</span>
            </div>
          ) : (
            <div
              className="flex animate-marquee hover:[animation-play-state:paused]"
              style={{ '--marquee-duration': `${duration}s` }}
            >
              {doubled.map((item, i) => (
                <div key={`${item.id}-${i}`} className="flex-shrink-0 flex items-center gap-2 px-4 py-1.5">
                  {renderItem(item)}
                  <span className={`${color} opacity-20`}>&bull;</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const LiveTicker = () => {
  const [muted, setMuted] = useState(() => localStorage.getItem('ra_muted') === '1');
  const [flashColor, setFlashColor] = useState(null);
  const [selectedSpotlight, setSelectedSpotlight] = useState(null);
  const flashTimerRef = useRef(null);

  const toggleMute = useCallback(() => {
    setMuted(prev => {
      const next = !prev;
      localStorage.setItem('ra_muted', next ? '1' : '0');
      if (!next) unlockAudio();
      return next;
    });
  }, []);

  const spotlightPollFn = useCallback(async () => {
    const r = await fetch(`${API}/api/spotlights/active`);
    if (r.ok) return r.json();
    return [];
  }, []);

  const musicPollFn = useCallback(async () => {
    const r = await fetch(`${API}/api/music-requests/board`);
    if (r.ok) return r.json();
    return [];
  }, []);

  const roomActionsPollFn = useCallback(async () => {
    const r = await fetch(`${API}/api/room-actions/active`);
    if (r.ok) return r.json();
    return [];
  }, []);

  const { items: spotlights } = useLiveFeed('spotlights', { pollFn: spotlightPollFn });
  const { items: musicRequests } = useLiveFeed('music', { pollFn: musicPollFn });
  const { items: roomActions } = useLiveFeed('room_actions', { pollFn: roomActionsPollFn });

  // ── Room Action Sound Effects + Flash ──
  const seenActionIdsRef = useRef(new Set());
  const initDoneRef = useRef(false);
  const mutedRef = useRef(muted);
  useEffect(() => { mutedRef.current = muted; }, [muted]);

  // Unlock audio on first user interaction
  useEffect(() => {
    const unlock = () => { unlockAudio(); window.removeEventListener('click', unlock); window.removeEventListener('touchstart', unlock); };
    window.addEventListener('click', unlock, { once: true });
    window.addEventListener('touchstart', unlock, { once: true });
    return () => { window.removeEventListener('click', unlock); window.removeEventListener('touchstart', unlock); };
  }, []);

  // Detect genuinely new room actions → play sound + flash
  useEffect(() => {
    if (!roomActions || roomActions.length === 0) return;
    const currentIds = new Set(roomActions.map(a => a.id));

    if (!initDoneRef.current) {
      seenActionIdsRef.current = currentIds;
      initDoneRef.current = true;
      return;
    }

    for (const action of roomActions) {
      if (!seenActionIdsRef.current.has(action.id)) {
        // Play sound (respects mute)
        if (!mutedRef.current) playRoomActionSound(action.action_type);
        // Flash the ticker row
        const color = ACTION_COLORS[action.action_type] || '#EF4444';
        setFlashColor(color);
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        flashTimerRef.current = setTimeout(() => setFlashColor(null), 850);
        break;
      }
    }
    seenActionIdsRef.current = currentIds;
  }, [roomActions]);

  // Cleanup flash timer
  useEffect(() => () => { if (flashTimerRef.current) clearTimeout(flashTimerRef.current); }, []);

  const last10Spotlights = spotlights.slice(0, 10);
  const last10Music = musicRequests.slice(0, 10);
  const last10Actions = roomActions.slice(0, 10);

  const MuteIcon = muted ? VolumeX : Volume2;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-black/90 backdrop-blur-xl" data-testid="live-ticker">
      {/* Flash keyframes */}
      <style>{`
        @keyframes ticker-flash {
          0% { background-color: color-mix(in srgb, var(--flash-color) 30%, transparent); }
          40% { background-color: color-mix(in srgb, var(--flash-color) 15%, transparent); }
          100% { background-color: transparent; }
        }
      `}</style>
      {/* Room Actions Ticker */}
      <TickerRow
        items={last10Actions}
        icon={Zap}
        color="text-[#EF4444]"
        bgColor="bg-[#EF4444]/5"
        borderColor="border-white/5"
        label="Room Actions"
        emptyText="No room actions yet"
        testId="ticker-room-actions"
        flashColor={flashColor}
        labelExtra={
          <button
            onClick={toggleMute}
            data-testid="ticker-mute-btn"
            className="ml-1.5 opacity-40 hover:opacity-80 transition-opacity"
            title={muted ? 'Unmute sounds' : 'Mute sounds'}
          >
            <MuteIcon size={9} className="text-[#EF4444]" />
          </button>
        }
        renderItem={(a) => {
          const actionColor = ACTION_COLORS[a.action_type] || '#EF4444';
          const target = a.action_type === 'one_mic_group'
            ? (a.target_group || '')
            : (a.target_name || '');
          const from = a.is_anonymous ? null : a.purchaser_name;
          return (
            <>
              <span className="text-[10px] font-bold uppercase" style={{ color: actionColor }}>{a.action_label || a.action_type}</span>
              <span className="text-white/30 text-[10px]">&#8212;</span>
              <span className="text-white/60 text-[10px] font-medium">{target}</span>
              {from && <span className="text-white/20 text-[9px]">from {from}</span>}
              {a.message && <span className="text-white/15 text-[9px] italic">&quot;{a.message.length > 40 ? a.message.slice(0, 40) + '...' : a.message}&quot;</span>}
            </>
          );
        }}
      />
      {/* Music Requests Ticker */}
      <TickerRow
        items={last10Music}
        icon={Music}
        color="text-[#8B5CF6]"
        bgColor="bg-[#8B5CF6]/5"
        borderColor="border-white/5"
        label="Requests"
        emptyText="No music requests yet"
        testId="ticker-music"
        renderItem={(r) => (
          <>
            <span className="text-white/60 text-[10px] font-medium">{r.song_title}</span>
            <span className="text-white/20 text-[10px]">&mdash;</span>
            <span className="text-[#8B5CF6]/70 text-[10px]">{r.artist_name}</span>
            {r.display_name && r.display_name !== 'Anonymous' && (
              <span className="text-white/15 text-[9px]">by {r.display_name}</span>
            )}
          </>
        )}
      />
      {/* Spotlights Ticker */}
      <TickerRow
        items={last10Spotlights}
        icon={Star}
        color="text-[#E91E8D]"
        bgColor="bg-[#E91E8D]/5"
        borderColor="border-white/5"
        label="Spotlights"
        emptyText="No spotlights yet"
        testId="ticker-spotlights"
        renderItem={(s) => {
          const name = s.is_anonymous ? 'ANONYMOUS' : (s.display_name || 'ANONYMOUS');
          const text = s.message.length > 80 ? s.message.slice(0, 80) + '...' : s.message;
          return (
            <button
              onClick={() => setSelectedSpotlight(s)}
              className="flex items-center gap-2 cursor-pointer group/spot"
              data-testid={`spotlight-item-${s.id}`}
            >
              <span className="text-[#E91E8D] text-[10px] font-bold uppercase">SPOTLIGHT</span>
              <span className="text-white/30 text-[10px]">&bull;</span>
              <span className="text-[#E91E8D]/60 text-[10px] font-semibold">{name}:</span>
              <span className="text-white/50 text-[10px] group-hover/spot:text-white/70 transition-colors">{text}</span>
              {s.link_url && <ExternalLink size={8} className="text-[#E91E8D]/40 flex-shrink-0" />}
            </button>
          );
        }}
      />
      {/* Spotlight Detail Modal */}
      {selectedSpotlight && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setSelectedSpotlight(null)} data-testid="spotlight-modal-overlay">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-sm bg-black/95 border border-[#E91E8D]/20 rounded-2xl p-6 shadow-2xl shadow-[#E91E8D]/10"
            onClick={e => e.stopPropagation()}
            data-testid="spotlight-modal"
          >
            <button onClick={() => setSelectedSpotlight(null)} className="absolute top-3 right-3 text-white/30 hover:text-white" data-testid="spotlight-modal-close">
              <X size={16} />
            </button>
            <div className="flex items-center gap-2 mb-3">
              <Star size={14} className="text-[#E91E8D]" />
              <span className="text-[#E91E8D] text-[10px] font-bold tracking-widest uppercase">Spotlight</span>
            </div>
            <p className="text-white/30 text-xs font-semibold mb-2">
              {selectedSpotlight.is_anonymous ? 'Anonymous' : (selectedSpotlight.display_name || 'Anonymous')}
            </p>
            <p className="text-white text-sm leading-relaxed mb-4" data-testid="spotlight-modal-message">
              {selectedSpotlight.message}
            </p>
            {selectedSpotlight.link_url && (
              <a
                href={selectedSpotlight.link_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#E91E8D]/10 border border-[#E91E8D]/20 text-[#E91E8D] text-xs font-semibold hover:bg-[#E91E8D]/20 transition-colors"
                data-testid="spotlight-modal-link"
              >
                <ExternalLink size={12} /> Visit Link
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveTicker;
